import { useEffect, useState } from "react";
import { TrayPopover } from "./TrayPopover";

// TODO: swap for backend-provided price_range meta once available
export const PRICE_BOUNDS = { min: 0, max: 100000 };

const THUMB_CLASSES =
  "pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 w-full appearance-none bg-transparent " +
  "[&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none " +
  "[&::-webkit-slider-thumb]:size-7 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white " +
  "[&::-webkit-slider-thumb]:shadow-[0px_0px_0px_1px_rgba(10,13,18,0.2),0px_1px_2px_0px_rgba(10,13,18,0.05)] " +
  "[&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none " +
  "[&::-moz-range-thumb]:size-7 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white " +
  "[&::-moz-range-thumb]:border-none " +
  "[&::-moz-range-thumb]:shadow-[0px_0px_0px_1px_rgba(10,13,18,0.2),0px_1px_2px_0px_rgba(10,13,18,0.05)]";

interface FilterSheetProps {
  open: boolean;
  min?: number;
  max?: number;
  onApply: (min: number, max: number) => void;
  onClose: () => void;
}

export const FilterSheet = ({ open, min, max, onApply, onClose }: FilterSheetProps) => {
  const [minVal, setMinVal] = useState(min ?? PRICE_BOUNDS.min);
  const [maxVal, setMaxVal] = useState(max ?? PRICE_BOUNDS.max);

  useEffect(() => {
    if (open) {
      setMinVal(min ?? PRICE_BOUNDS.min);
      setMaxVal(max ?? PRICE_BOUNDS.max);
    }
  }, [open, min, max]);

  const range = PRICE_BOUNDS.max - PRICE_BOUNDS.min;
  const minPercent = ((minVal - PRICE_BOUNDS.min) / range) * 100;
  const maxPercent = ((maxVal - PRICE_BOUNDS.min) / range) * 100;

  const clampMin = (value: number) => Math.min(Math.max(value, PRICE_BOUNDS.min), maxVal);
  const clampMax = (value: number) => Math.max(Math.min(value, PRICE_BOUNDS.max), minVal);

  const priceInputs = [
    { label: "From", value: minVal, set: (v: number) => setMinVal(clampMin(v)) },
    { label: "Upto", value: maxVal, set: (v: number) => setMaxVal(clampMax(v)) },
  ] as const;

  return (
    <TrayPopover open={open} title="Price range" onClose={onClose}>
      <div className="relative h-10 px-1 ">
        <div className="absolute inset-x-1 top-1/2 h-2.5 -translate-y-1/2 overflow-hidden rounded-full bg-black/[0.06]">
          <div
            className="absolute inset-y-[2px] bg-primary-dark"
            style={{ left: `${minPercent}%`, right: `${100 - maxPercent}%` }}
          />
        </div>
        <input
          type="range"
          min={PRICE_BOUNDS.min}
          max={PRICE_BOUNDS.max}
          value={minVal}
          onChange={(e) => setMinVal(clampMin(Number(e.target.value)))}
          className={THUMB_CLASSES}
        />
        <input
          type="range"
          min={PRICE_BOUNDS.min}
          max={PRICE_BOUNDS.max}
          value={maxVal}
          onChange={(e) => setMaxVal(clampMax(Number(e.target.value)))}
          className={THUMB_CLASSES}
        />
      </div>

      <div className="flex gap-3 pt-5">
        {priceInputs.map(({ label, value, set }) => (
          <label key={label} className="flex flex-1 flex-col gap-1">
            <span className="px-1 text-text-sm-medium text-gray-dark">{label}</span>
            <div className="flex items-center gap-1 rounded-xl bg-white p-3 shadow-[0px_0px_0px_1px_rgba(10,13,18,0.1),0px_1px_2px_0px_rgba(10,13,18,0.05)] focus-within:shadow-[0px_0px_0px_1.5px_var(--flo-primary-dark-color),0px_0px_0px_1px_rgba(10,13,18,0.1),0px_1px_2px_0px_rgba(10,13,18,0.05)]">
              <span className="text-text-md-regular text-coal-dark opacity-50">₹</span>
              <input
                type="number"
                inputMode="numeric"
                value={value}
                onChange={(e) => set(Number(e.target.value) || 0)}
                className="w-full bg-transparent text-text-md-regular text-coal-dark outline-none"
              />
            </div>
          </label>
        ))}
      </div>

      <button
        type="button"
        onClick={() => onApply(minVal, maxVal)}
        className="mt-7 h-[52px] w-full rounded-xl bg-primary-dark text-text-md-semibold text-btnPrimaryText active:scale-[0.98] transition-transform">
        Apply
      </button>
    </TrayPopover>
  );
};
